import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, Lock, ArrowLeft, BookOpen } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(null));
  }, [slug]);

  if (post === undefined)
    return <div className="min-h-screen bp-grid"><Navbar /><div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div></div>;

  if (post === null)
    return (
      <div className="min-h-screen bp-grid"><Navbar />
        <div className="mx-auto flex max-w-md flex-col items-center px-4 py-28 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-heading text-2xl font-bold">Post not found</h1>
          <Link to="/blog" className="mt-6"><Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black">Back to blog</Button></Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-8">
        <Link to="/blog" className="inline-flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4" /> Blog
        </Link>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {post.vip_only && <span className="inline-flex items-center gap-1 border border-primary px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary"><Crown className="h-3 w-3" /> VIP</span>}
          <span className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">{post.author_name} · {fmt(post.created_at)}</span>
        </div>
        <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tighter">{post.title}</h1>
        {post.cover_url && (
          <div className="mt-6 aspect-[16/9] overflow-hidden border border-border bg-[#050a16]">
            <img src={post.cover_url} alt={post.title} className="h-full w-full object-cover" />
          </div>
        )}

        {post.locked ? (
          <div className="mt-8 border border-border border-l-2 border-l-primary bg-secondary/20 p-8 text-center" data-testid="vip-lock">
            <Lock className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-4 font-heading text-2xl font-bold">This is a VIP-only guide</h2>
            <p className="mx-auto mt-2 max-w-md text-muted-foreground">{post.excerpt}</p>
            <Link to="/billing" className="mt-6 inline-block" data-testid="unlock-vip-cta">
              <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                <Crown className="mr-2 h-4 w-4" /> Unlock with VIP
              </Button>
            </Link>
          </div>
        ) : (
          <div className="prose mt-8 max-w-none">
            {post.body.split("\n\n").map((para, i) => (
              <p key={i} className="mb-5 whitespace-pre-wrap text-[17px] leading-relaxed text-foreground">{para}</p>
            ))}
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
